
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import { CommonPensionCalculations, FormOfPaymentOptions } from 'app/calcs/pensions'
import { ICommonPensionInputs, IMonthlyPensionPayments, SimplePensionFormOfPayment, SimplePensionFormOfPaymentKinds } from 'app/calcs/pensions/interfaces'
import { SpousalIncomeType } from 'app/calcs/pensions/spousal/interfaces'
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { ageAtAsDuration, ageToDuration, dateAtAge, deadBy, expectedDeathYear, IRetirementPerson, isValidPerson, Profile } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import * as DateCalcs from 'app/utils/DateCalcs'
import * as _ from 'lodash'
import * as moment from 'moment'

/**
 * Define the information needed to calculate a [simple]
 * spousal pension.
 */
interface ISpousalPensionInputs extends ICommonPensionInputs {
  monthlySalary: number
  ageSalaryStops: moment.Duration
  pensionAmount: number
  kind: SimplePensionFormOfPayment
}

interface ISpousalPensionIncome {
  age: moment.Duration,
  amount: number
}

const spouseSource = 'spouse-pension'

class SpousalPensionCalculations extends CommonPensionCalculations<ISpousalPensionInputs> {

  constructor(profile: Profile) {
    super(profile, '')
  }

  getMonthlyIncome(person: IRetirementPerson, vals: ISpousalPensionInputs): Promise<IMonthlyPensionPayments> {
    const monthlySalary = vals.monthlySalary || 0
    return Promise.resolve({
      amount: monthlySalary,
      kind: SpousalIncomeType.salary,
      beginPaymentsAge: vals.beginPaymentsAge,
      totalLumpSum: 0,
      totalVestingService: 0,
      totalPensionCredits: 0
    })
  }

  /**
   * Calculate the monthly payment for an ordinary ordinary spousal pension.
   *
   * @param request The parameters needed to perform the calculations
   *
   * @returns The IMonthlyPensionAmounts
   */
  getMonthlyPensionAmount(person: IRetirementPerson, vals: ISpousalPensionInputs, when: moment.Moment): Promise<IMonthlyPensionPayments> {
    return Promise.resolve(this.calculateSimplePension(when, vals.pensionAmount, vals.kind, true))
  }

  getPension(): ISpousalPensionInputs {
    return this.profile.pension.spousal
  }

  getPerson(): IRetirementPerson {
    return this.profile.getSpouse()
  }

  getTimeLineTransactions(): Promise<TX.ITransactionItem[]> {
    const workers = []
    const r: TX.ITransactionItem[] = []
    const w1 = super.getTimeLineTransactions()
      .then((rr) => r.push(...rr))
    workers.push(w1)

    do {
      // tslint:disable-next-line:no-debugger
      //debugger
      const spouse = this.getPerson()
      const spousalPension = this.getPension()
      if (!spouse || !spousalPension || !isValidPerson(spouse)) { break }

      // the final salary is either when the spouse dies or stops earning
      const stopAge = DateCalcs.minDuration(spousalPension.ageSalaryStops, ageToDuration(spouse.selectedAgeExpectancy))
      const stopWhen = dateAtAge(spouse, stopAge)
      const monthlySalary = spousalPension.monthlySalary || 0
      if (0 !== monthlySalary) {
        r.push({
          when: stopWhen,
          section: 'Pension',
          source: spouseSource,
          who: spouse.firstName,
          what: { [SpousalIncomeType.salary]: monthlySalary },
          description: utils.personPossessive(spouse.firstName) + ' ' + 'final monthly salary at age ' + utils.toNiceAge(stopAge) + ' of <a>' + utils.toCurrency(monthlySalary) + '</a>'
        })
      }
    } while (false)

    return Promise.all(workers)
      .then((x) => r)
  }

  getTransactions(when: moment.Moment, tolerance: TX.TransactionTolerance = 'month'): Promise<TX.ITransactionItem[]> {
    const workers = []
    const r: TX.ITransactionItem[] = []
    const w1 = super.getTransactions(when, tolerance)
      .then((rr) => r.push(...rr))
    workers.push(w1)

    /* 7/17 removed-- not needed in transactions...
    do {
      const spouse = this.getPerson()
      const spousalPension = this.getPension()
      if (!spouse || !spousalPension || !isValidPerson(spouse)) { break }

      if (!DateCalcs.isAlive(spouse, when)) { break }
      if (!DateCalcs.isOlderThan(spouse, spousalPension.ageSalaryStops, when)) { break }

      const w2 = this.getMonthlyIncome(spouse, spousalPension)
        .then((mi) => {
          if (0 === mi.amount) { return false }
          const kind = SpousalIncomeType[mi.kind]
          r.push({
            when,
            section: 'Pension',
            source: spouseSource,
            who: spouse.firstName,
            what: { [kind]: mi.amount },
            description: utils.personPossessive(spouse.firstName) + ' ' + 'salary at age ' + utils.toNiceAge(ageAtAsDuration(spouse, when))
          })
        })

      workers.push(w2)
    } while (false)
    */

    return Promise.all(workers)
      .then((x) => r)
  }

}

export {
  ISpousalPensionIncome,
  ISpousalPensionInputs,
  SpousalIncomeType,
  SpousalPensionCalculations
}



// WEBPACK FOOTER //
// ./src/app/calcs/pensions/spousal/index.ts